import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MsalModule, MsalRedirectComponent, MsalGuard, MsalInterceptor } from "@azure/msal-angular";
import { PublicClientApplication, InteractionType } from "@azure/msal-browser";
import { CommonModule, DatePipe, HashLocationStrategy, LocationStrategy, PathLocationStrategy } from '@angular/common';

import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';

import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NavMenuComponent } from './nav-menu/nav-menu.component';
import { PqgridComponent } from './pqgrid/pqgrid.component';
import { SchedulingComponent } from './scheduling/scheduling.component';
import { StagingComponent } from './scheduling/staging.component';
import { SchedulesComponent } from './scheduling/schedules.component';
import { ScheduleFormComponent } from './schedule-form/schedule-form.component';
import { DispatchFormComponent } from './dispatch-form/dispatch-form.component';
import { CopyDispatchComponent } from './copy-dispatch/copy-dispatch.component';
import { ConfirmDispatchComponent } from './trucker-confirmation/confirm-dispatch.component';
import { ConfirmTruckersComponent } from './trucker-confirmation/confirm-truckers.component';
import { LoadsStatusComponent } from './trucker-confirmation/loads-status.component';
import { TruckerConfirmReportComponent } from './trucker-confirmation/trucker-confirm-report.component';
import { ContractsComponent } from './contracts/contracts.component';
import { TruckersComponent } from './truckers/truckers.component';
import { LoadBankComponent } from './load-bank/load-bank.component';
import { LoadBankApplyComponent } from './load-bank-apply/load-bank-apply.component';
import { UserSettingsComponent } from './user-settings/user-settings.component';
import { StagingContractsComponent } from './scheduling/staging-contracts.component';
import { TruckerSummaryComponent } from './trucker-summary/trucker-summary.component';
import { ToastNotificationComponent } from './toast-notification/toast-notification.component';
 
import { ScheduleMobileComponent } from './mobile-screens/schedule-mobile/schedule-mobile.component';
import { FooterMobileComponent } from './mobile-screens/footer-mobile/footer-mobile.component';
import { ViewDispatchComponent } from './mobile-screens/view-dispatch/view-dispatch.component';
import { LoadUnloadComponent } from './mobile-screens/load-unload/load-unload.component';
import { AlertTruckersFilterComponent } from './mobile-screens/alert-truckers-filter/alert-truckers-filter.component';
import { MobileMoveSplitDispatchFormComponent } from './mobile-screens/mobile-move-split-dispatch-form/mobile-move-split-dispatch-form.component';

import { AlertTruckerFormComponent } from './alert-trucker-form/alert-trucker-form.component'; 
import { MoveSplitDispatchFormComponent } from './move-split-dispatch-form/move-split-dispatch-form.component';
import { EmailQuestionFormComponent } from './email-question/email-question-form.component';
import { RoundFormComponent } from './round-form/round-form.component';
import { DeleteComponent } from './delete-confirm/delete-confirm.component';
import { CopyRoundComponent } from './copy-round/copy-round.component'; 
import { LocationConfirmationComponent } from './location-confirmation/location-confirmation.component';
import { LocationConfirmReportComponent } from './location-confirmation/location-confirm-report.component';
import { TicketMaintenanceComponent } from './ticket-maintenance/ticket-maintenance.component';
import { TMLoadsComponent } from './ticket-maintenance/tm-loads.component';
import { HeaderMobileComponent } from './mobile-screens/header-mobile/header-mobile.component';
import { LoadListComponent } from './mobile-screens/load-list/load-list.component';
import { environment } from '../environments/environment';
import { ScaleViewComponent } from './scale-view/scale-view.component';
import { ScoutAiDrawerComponent } from './scout-ai/scout-ai-drawer.component';
import { ScoutAiPopoutComponent } from './scout-ai/scout-ai-popout.component';
import { ScoutAiSessionComponent } from './scout-ai/scout-ai-session.component';
import { ScoutAiMessageViewComponent } from './scout-ai/scout-ai-message-view.component';
import { MarkdownPipe } from './scout-ai/pipes/markdown.pipe';

import {
    NgxUiLoaderModule,
    NgxUiLoaderConfig,
    SPINNER,
    POSITION,
    PB_DIRECTION,
  } from "ngx-ui-loader";
import { TextsStatusComponent } from './trucker-confirmation/texts-status.component';
import { AdminConfigComponent } from './admin-config/admin-config.component';
import { AcsUserManagementComponent } from './admin-config/acs-user-management/acs-user-management.component';
import { AcsTradeUnitDefaultComponent } from './admin-config/acs-trade-unit-default/acs-trade-unit-default.component';
import { AcsLocationNotesComponent } from './admin-config/acs-location-notes/acs-location-notes.component';
import { AcsElevatorMatchComponent } from './admin-config/acs-elevator-match/acs-elevator-match.component';
import { AcsBillAddressComponent } from './admin-config/acs-bill-address/acs-bill-address.component';
import { AcsDeleteCustomerComponent } from './admin-config/acs-delete-customer/acs-delete-customer.component';
import { AcsVariableWeightComponent } from './admin-config/acs-variable-weight/acs-variable-weight.component';
import { AcsTradeUnitGroupsComponent } from './admin-config/acs-trade-unit-groups/acs-trade-unit-groups.component';
import { AcsTradeUnitCommodityComponent } from './admin-config/acs-trade-unit-commodity/acs-trade-unit-commodity.component';


const ngxUiLoaderConfig: NgxUiLoaderConfig = {
    bgsColor: "red",
    bgsPosition: POSITION.bottomCenter,
    bgsSize: 40,
    bgsType: SPINNER.rectangleBounce, // background spinner type
    fgsType: SPINNER.chasingDots, // foreground spinner type
    pbDirection: PB_DIRECTION.leftToRight, // progress bar direction
    pbThickness: 5, // progress bar thickness
};

const isIE =
    window.navigator.userAgent.indexOf("MSIE ") > -1 ||
    window.navigator.userAgent.indexOf("Trident/") > -1;

@NgModule({ declarations: [
        AppComponent,
        NavMenuComponent,
        PqgridComponent,
        SchedulingComponent,
        StagingComponent,
        StagingContractsComponent,
        SchedulesComponent,
        ScheduleFormComponent,
        DispatchFormComponent,
        CopyDispatchComponent,
        ConfirmDispatchComponent,
        ConfirmTruckersComponent,
        LoadsStatusComponent,
        TruckerConfirmReportComponent,
        TruckerSummaryComponent,
        UserSettingsComponent,
        ContractsComponent,
        TruckersComponent,
        LoadBankComponent,
        LoadBankApplyComponent,
        ScheduleMobileComponent,
        FooterMobileComponent,
        ViewDispatchComponent,
        LoadUnloadComponent,
        AlertTruckersFilterComponent,
        MobileMoveSplitDispatchFormComponent,
        AlertTruckerFormComponent,
        MoveSplitDispatchFormComponent,
        EmailQuestionFormComponent,
        ToastNotificationComponent,
        TicketMaintenanceComponent,
        DeleteComponent,
        RoundFormComponent,
        CopyRoundComponent,
        LocationConfirmationComponent,
        LocationConfirmReportComponent,
        TMLoadsComponent,
        HeaderMobileComponent,
        LoadListComponent,
        TextsStatusComponent,
        AdminConfigComponent,
        AcsUserManagementComponent,
        AcsTradeUnitDefaultComponent,
        AcsLocationNotesComponent,
        AcsElevatorMatchComponent,
        AcsBillAddressComponent,
        AcsDeleteCustomerComponent,
        AcsVariableWeightComponent,
        AcsTradeUnitGroupsComponent,
        AcsTradeUnitCommodityComponent,
        ScaleViewComponent,
        ScoutAiDrawerComponent,
        ScoutAiPopoutComponent,
        ScoutAiSessionComponent,
        ScoutAiMessageViewComponent,
        MarkdownPipe
    ],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        NgxMaskDirective,
        NgxMaskPipe,
        MatFormFieldModule, MatSelectModule, MatInputModule, MatTableModule,
        MatPaginatorModule, MatCardModule, MatButtonModule, MatGridListModule, MatIconModule,
        NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
        MsalModule.forRoot(new PublicClientApplication({
            auth: {
                clientId: environment.clientId,
                authority: environment.loginUrl,
                redirectUri: environment.baseUri
            },
            cache: {
                cacheLocation: 'localStorage',
                storeAuthStateInCookie: isIE,
            },
        }), {
            interactionType: InteractionType.Redirect,
            authRequest: {
                scopes: ['user.read'],
            },
        }, {
            interactionType: InteractionType.Redirect,
            protectedResourceMap: new Map([
                ['https://graph.microsoft.com/v1.0/me', ['user.read']],
                [environment.baseUri + '/api', [environment.authScope]],
            ]),
        })], providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        { provide: LocationStrategy, useClass: HashLocationStrategy },
        MsalGuard,
        DatePipe,
        provideNgxMask(),
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }

